/**
 * useAdmin Hook
 *
 * React Query hooks for admin/lookup table operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import { adminService } from '../services/admin.service';
import { unwrapResponse } from '../types';

import type {
  Company,
  Portco,
  Segment,
  MeasureType,
  SponsorType,
  ScoreCategory,
  CostCategory,
  Score,
} from '@medsuite/models';

export const adminKeys = {
  companies: ['admin', 'companies'] as const,
  portcos: (companyId?: number) => ['admin', 'portcos', companyId] as const,
  segments: ['admin', 'segments'] as const,
  measureTypes: (costOrBenefit?: 'C' | 'B') => ['admin', 'measure-types', costOrBenefit] as const,
  sponsorTypes: ['admin', 'sponsor-types'] as const,
  scoreCategories: ['admin', 'score-categories'] as const,
  costCategories: ['admin', 'cost-categories'] as const,
  scores: (categoryId?: number) => ['admin', 'scores', categoryId] as const,
  scoresGrouped: ['admin', 'scores', 'grouped'] as const,
};

/**
 * Companies hooks
 */
export function useCompanies(
  options?: Omit<UseQueryOptions<Company[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Company[], Error>({
    queryKey: adminKeys.companies,
    queryFn: async () => {
      const response = await adminService.companies.getAll();
      // Extract data from success wrapper: {success: true, data: [...], meta: {}}
      return unwrapResponse(response.data);
    },
    ...options,
  });
}

/**
 * Create company mutation
 */
export function useCreateCompany(
  options?: UseMutationOptions<Company, Error, { company_name: string }>
) {
  const queryClient = useQueryClient();

  return useMutation<Company, Error, { company_name: string }>({
    mutationFn: async (data) => {
      const response = await adminService.companies.create(data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.companies });
    },
    ...options,
  });
}

/**
 * Update company mutation
 */
export function useUpdateCompany(
  options?: UseMutationOptions<Company, Error, { id: number; data: { company_name: string } }>
) {
  const queryClient = useQueryClient();

  return useMutation<Company, Error, { id: number; data: { company_name: string } }>({
    mutationFn: async ({ id, data }) => {
      const response = await adminService.companies.update(id, data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.companies });
    },
    ...options,
  });
}

/**
 * Delete company mutation
 */
export function useDeleteCompany(options?: UseMutationOptions<void, Error, number>) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await adminService.companies.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.companies });
    },
    ...options,
  });
}

/**
 * Portfolio companies hooks
 */
export function usePortcos(
  companyId?: number,
  options?: Omit<UseQueryOptions<Portco[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Portco[], Error>({
    queryKey: adminKeys.portcos(companyId),
    queryFn: async () => {
      const response = await adminService.portcos.getAll({ companyId });
      // Extract data from success wrapper: {success: true, data: [...], meta: {}}
      return unwrapResponse(response.data);
    },
    ...options,
  });
}

/**
 * Create portco mutation
 */
export function useCreatePortco(
  options?: UseMutationOptions<Portco, Error, { portco_name: string; company_id: number }>
) {
  const queryClient = useQueryClient();

  return useMutation<Portco, Error, { portco_name: string; company_id: number }>({
    mutationFn: async (data) => {
      const response = await adminService.portcos.create(data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'portcos'] });
    },
    ...options,
  });
}

/**
 * Update portco mutation
 */
export function useUpdatePortco(
  options?: UseMutationOptions<
    Portco,
    Error,
    { id: number; data: Partial<{ portco_name: string; company_id: number }> }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<
    Portco,
    Error,
    { id: number; data: Partial<{ portco_name: string; company_id: number }> }
  >({
    mutationFn: async ({ id, data }) => {
      const response = await adminService.portcos.update(id, data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'portcos'] });
    },
    ...options,
  });
}

/**
 * Delete portco mutation
 */
export function useDeletePortco(options?: UseMutationOptions<void, Error, number>) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await adminService.portcos.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'portcos'] });
    },
    ...options,
  });
}

/**
 * Segments hooks
 */
export function useSegments(
  options?: Omit<UseQueryOptions<Segment[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Segment[], Error>({
    queryKey: adminKeys.segments,
    queryFn: async () => {
      const response = await adminService.segments.getAll();
      // Extract data from success wrapper: {success: true, data: [...], meta: {}}
      return unwrapResponse(response.data);
    },
    ...options,
  });
}

/**
 * Create segment mutation
 */
export function useCreateSegment(
  options?: UseMutationOptions<Segment, Error, { segment_name: string; program_name: string }>
) {
  const queryClient = useQueryClient();

  return useMutation<Segment, Error, { segment_name: string; program_name: string }>({
    mutationFn: async (data) => {
      const response = await adminService.segments.create(data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.segments });
    },
    ...options,
  });
}

/**
 * Update segment mutation
 */
export function useUpdateSegment(
  options?: UseMutationOptions<
    Segment,
    Error,
    { id: number; data: Partial<{ segment_name: string; program_name: string }> }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<
    Segment,
    Error,
    { id: number; data: Partial<{ segment_name: string; program_name: string }> }
  >({
    mutationFn: async ({ id, data }) => {
      const response = await adminService.segments.update(id, data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.segments });
    },
    ...options,
  });
}

/**
 * Delete segment mutation
 */
export function useDeleteSegment(options?: UseMutationOptions<void, Error, number>) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await adminService.segments.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.segments });
    },
    ...options,
  });
}

/**
 * Measure types hooks
 */
export function useMeasureTypes(
  costOrBenefit?: 'C' | 'B',
  options?: Omit<UseQueryOptions<MeasureType[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<MeasureType[], Error>({
    queryKey: adminKeys.measureTypes(costOrBenefit),
    queryFn: async () => {
      const response = await adminService.measureTypes.getAll({ costOrBenefit });
      // Extract data from success wrapper: {success: true, data: [...], meta: {}}
      return unwrapResponse(response.data);
    },
    ...options,
  });
}

/**
 * Create measure type mutation
 */
export function useCreateMeasureType(
  options?: UseMutationOptions<
    MeasureType,
    Error,
    { measure_type_name: string; cost_or_benefit: 'C' | 'B' }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<MeasureType, Error, { measure_type_name: string; cost_or_benefit: 'C' | 'B' }>(
    {
      mutationFn: async (data) => {
        const response = await adminService.measureTypes.create(data);
        return unwrapResponse(response.data);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['admin', 'measure-types'] });
      },
      ...options,
    }
  );
}

/**
 * Update measure type mutation
 */
export function useUpdateMeasureType(
  options?: UseMutationOptions<
    MeasureType,
    Error,
    { id: number; data: Partial<{ measure_type_name: string; cost_or_benefit: 'C' | 'B' }> }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<
    MeasureType,
    Error,
    { id: number; data: Partial<{ measure_type_name: string; cost_or_benefit: 'C' | 'B' }> }
  >({
    mutationFn: async ({ id, data }) => {
      const response = await adminService.measureTypes.update(id, data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'measure-types'] });
    },
    ...options,
  });
}

/**
 * Delete measure type mutation
 */
export function useDeleteMeasureType(options?: UseMutationOptions<void, Error, number>) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await adminService.measureTypes.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'measure-types'] });
    },
    ...options,
  });
}

/**
 * Frontend sponsor type with 'sequence' field (mapped from API's sponsor_type_sequence)
 */
interface SponsorTypeWithSequence extends Omit<SponsorType, 'sponsor_type_sequence'> {
  sequence: number;
}

/**
 * Sponsor types hooks
 * Maps API 'sponsor_type_sequence' to frontend 'sequence' field
 */
export function useSponsorTypes(
  options?: Omit<UseQueryOptions<SponsorTypeWithSequence[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<SponsorTypeWithSequence[], Error>({
    queryKey: adminKeys.sponsorTypes,
    queryFn: async () => {
      const response = await adminService.sponsorTypes.getAll();
      // Extract data from success wrapper: {success: true, data: [...], meta: {}}
      const data = unwrapResponse<(SponsorType & { sponsor_type_sequence?: number })[]>(response.data);
      // Map sponsor_type_sequence to sequence for frontend
      return data.map((item) => ({
        sponsor_type_id: item.sponsor_type_id,
        sponsor_type_name: item.sponsor_type_name,
        is_required: item.is_required,
        sequence: item.sponsor_type_sequence ?? 0,
      }));
    },
    ...options,
  });
}

/**
 * Create sponsor type mutation
 * Maps frontend 'sequence' field to API 'sponsor_type_sequence'
 */
export function useCreateSponsorType(
  options?: UseMutationOptions<
    SponsorType,
    Error,
    { sponsor_type_name: string; is_required: boolean; sequence?: number }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<
    SponsorType,
    Error,
    { sponsor_type_name: string; is_required: boolean; sequence?: number }
  >({
    mutationFn: async (data) => {
      // Map frontend 'sequence' to API 'sponsor_type_sequence'
      const apiData = {
        sponsor_type_name: data.sponsor_type_name,
        is_required: data.is_required,
        sponsor_type_sequence: data.sequence,
      };
      const response = await adminService.sponsorTypes.create(apiData);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.sponsorTypes });
    },
    ...options,
  });
}

/**
 * Update sponsor type mutation
 * Maps frontend 'sequence' field to API 'sponsor_type_sequence'
 */
export function useUpdateSponsorType(
  options?: UseMutationOptions<
    SponsorType,
    Error,
    {
      id: number;
      data: Partial<{ sponsor_type_name: string; is_required: boolean; sequence: number }>;
    }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<
    SponsorType,
    Error,
    {
      id: number;
      data: Partial<{ sponsor_type_name: string; is_required: boolean; sequence: number }>;
    }
  >({
    mutationFn: async ({ id, data }) => {
      // Map frontend 'sequence' to API 'sponsor_type_sequence'
      const apiData: Partial<{
        sponsor_type_name: string;
        is_required: boolean;
        sponsor_type_sequence: number;
      }> = {};
      if (data.sponsor_type_name !== undefined) {
        apiData.sponsor_type_name = data.sponsor_type_name;
      }
      if (data.is_required !== undefined) {
        apiData.is_required = data.is_required;
      }
      if (data.sequence !== undefined) {
        apiData.sponsor_type_sequence = data.sequence;
      }
      const response = await adminService.sponsorTypes.update(id, apiData);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.sponsorTypes });
    },
    ...options,
  });
}

/**
 * Delete sponsor type mutation
 */
export function useDeleteSponsorType(options?: UseMutationOptions<void, Error, number>) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await adminService.sponsorTypes.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.sponsorTypes });
    },
    ...options,
  });
}

/**
 * Reorder sponsor types
 */
export function useReorderSponsorTypes(
  options?: UseMutationOptions<SponsorType[], Error, number[]>
) {
  const queryClient = useQueryClient();

  return useMutation<SponsorType[], Error, number[]>({
    mutationFn: async (sponsorTypeIds) => {
      const response = await adminService.sponsorTypes.reorder(sponsorTypeIds);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.sponsorTypes });
    },
    ...options,
  });
}

/**
 * Score categories hooks
 */
export function useScoreCategories(
  options?: Omit<UseQueryOptions<ScoreCategory[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<ScoreCategory[], Error>({
    queryKey: adminKeys.scoreCategories,
    queryFn: async () => {
      const response = await adminService.scoreCategories.getAll();
      // Extract data from success wrapper: {success: true, data: [...], meta: {}}
      return unwrapResponse(response.data);
    },
    ...options,
  });
}

/**
 * Create score category mutation
 */
export function useCreateScoreCategory(
  options?: UseMutationOptions<
    ScoreCategory,
    Error,
    { score_category_name: string; selection_type?: 'S' | 'M' }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<
    ScoreCategory,
    Error,
    { score_category_name: string; selection_type?: 'S' | 'M' }
  >({
    mutationFn: async (data) => {
      const response = await adminService.scoreCategories.create(data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.scoreCategories });
    },
    ...options,
  });
}

/**
 * Update score category mutation
 */
export function useUpdateScoreCategory(
  options?: UseMutationOptions<
    ScoreCategory,
    Error,
    { id: number; data: Partial<{ score_category_name: string; selection_type: 'S' | 'M' }> }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<
    ScoreCategory,
    Error,
    { id: number; data: Partial<{ score_category_name: string; selection_type: 'S' | 'M' }> }
  >({
    mutationFn: async ({ id, data }) => {
      const response = await adminService.scoreCategories.update(id, data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.scoreCategories });
    },
    ...options,
  });
}

/**
 * Delete score category mutation
 */
export function useDeleteScoreCategory(options?: UseMutationOptions<void, Error, number>) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await adminService.scoreCategories.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.scoreCategories });
    },
    ...options,
  });
}

/**
 * Cost categories hooks
 */
export function useCostCategories(
  options?: Omit<UseQueryOptions<CostCategory[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<CostCategory[], Error>({
    queryKey: adminKeys.costCategories,
    queryFn: async () => {
      const response = await adminService.costCategories.getAll();
      // Extract data from success wrapper: {success: true, data: [...], meta: {}}
      return unwrapResponse(response.data);
    },
    ...options,
  });
}

/**
 * Create cost category mutation
 */
export function useCreateCostCategory(
  options?: UseMutationOptions<CostCategory, Error, { cost_category_name: string }>
) {
  const queryClient = useQueryClient();

  return useMutation<CostCategory, Error, { cost_category_name: string }>({
    mutationFn: async (data) => {
      const response = await adminService.costCategories.create(data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.costCategories });
    },
    ...options,
  });
}

/**
 * Update cost category mutation
 */
export function useUpdateCostCategory(
  options?: UseMutationOptions<
    CostCategory,
    Error,
    { id: number; data: { cost_category_name: string } }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<CostCategory, Error, { id: number; data: { cost_category_name: string } }>({
    mutationFn: async ({ id, data }) => {
      const response = await adminService.costCategories.update(id, data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.costCategories });
    },
    ...options,
  });
}

/**
 * Delete cost category mutation
 */
export function useDeleteCostCategory(options?: UseMutationOptions<void, Error, number>) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await adminService.costCategories.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.costCategories });
    },
    ...options,
  });
}

/**
 * Scores hooks (admin lookup table for score templates)
 */
export function useAdminScores(
  categoryId?: number,
  options?: Omit<UseQueryOptions<Score[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Score[], Error>({
    queryKey: adminKeys.scores(categoryId),
    queryFn: async () => {
      const response = await adminService.scores.getAll({ categoryId });
      // Extract data from success wrapper: {success: true, data: [...], meta: {}}
      return unwrapResponse(response.data);
    },
    ...options,
  });
}

/**
 * Scores grouped by category
 */
export function useScoresGrouped(
  options?: Omit<UseQueryOptions<{ [categoryId: number]: Score[] }, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<{ [categoryId: number]: Score[] }, Error>({
    queryKey: adminKeys.scoresGrouped,
    queryFn: async () => {
      const response = await adminService.scores.getGrouped();
      // Extract data from success wrapper: {success: true, data: {...}, meta: {}}
      return unwrapResponse(response.data);
    },
    ...options,
  });
}

/**
 * Create score mutation
 */
export function useCreateScore(
  options?: UseMutationOptions<Score, Error, Parameters<typeof adminService.scores.create>[0]>
) {
  const queryClient = useQueryClient();

  return useMutation<Score, Error, Parameters<typeof adminService.scores.create>[0]>({
    mutationFn: async (data) => {
      const response = await adminService.scores.create(data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'scores'] });
    },
    ...options,
  });
}

/**
 * Update score mutation
 */
export function useUpdateScore(
  options?: UseMutationOptions<
    Score,
    Error,
    { id: number; data: Parameters<typeof adminService.scores.update>[1] }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<
    Score,
    Error,
    { id: number; data: Parameters<typeof adminService.scores.update>[1] }
  >({
    mutationFn: async ({ id, data }) => {
      const response = await adminService.scores.update(id, data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'scores'] });
    },
    ...options,
  });
}

/**
 * Delete score mutation
 */
export function useDeleteScore(options?: UseMutationOptions<void, Error, number>) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await adminService.scores.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'scores'] });
    },
    ...options,
  });
}

/**
 * Reorder score mutation
 */
export function useReorderScore(
  options?: UseMutationOptions<Score[], Error, { id: number; direction: 'up' | 'down' }>
) {
  const queryClient = useQueryClient();

  return useMutation<Score[], Error, { id: number; direction: 'up' | 'down' }>({
    mutationFn: async ({ id, direction }) => {
      const response = await adminService.scores.reorder(id, direction);
      const unwrapped = unwrapResponse<{ scores?: Score[] }>(response.data);
      return unwrapped?.scores || (response.data as unknown as Score[]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'scores'] });
    },
    ...options,
  });
}
